// 解析订单上的原始 cardPayInfo（后端未结构化）——尽力从 JSON / key=value / 自由文本中
// 提取 卡品牌 · 后四位 · 读卡方式 · 授权码 · 参考号；无法识别时保留原文。
// 后端确认 cardPayInfo 结构后，可直接映射字段，无需改动调用方。

const BRAND_PATTERNS = [
  [/union\s*pay|银联/i, "UnionPay"],
  [/master\s*card|master/i, "Mastercard"],
  [/visa/i, "Visa"],
  [/amex|american\s*express/i, "Amex"],
  [/discover/i, "Discover"],
  [/maestro/i, "Maestro"],
  [/jcb/i, "JCB"],
  [/diners/i, "Diners"]
];

const detectBrand = str => {
  for (const [re, name] of BRAND_PATTERNS) {
    if (re.test(str)) {
      return name;
    }
  }
  return null;
};

const titleBrand = b => {
  const hit = detectBrand(String(b));
  return hit || String(b);
};

const normalizeCard = (obj, rawStr) => {
  const lowerMap = {};
  Object.keys(obj || {}).forEach(k => {
    lowerMap[String(k).toLowerCase()] = obj[k];
  });
  const get = (...keys) => {
    for (const k of keys) {
      if (lowerMap[k] !== undefined && lowerMap[k] !== null && lowerMap[k] !== "") {
        return lowerMap[k];
      }
    }
    return undefined;
  };

  let last4 = get("last4", "lastfour", "last_four", "cardlast4", "pan4", "tail");
  const pan = get("pan", "cardno", "cardnumber", "card_number", "maskedpan", "card");
  if (!last4 && pan) {
    const digits = String(pan).replace(/\D/g, "");
    if (digits.length >= 4) {
      last4 = digits.slice(-4);
    }
  }
  if (!last4 && rawStr) {
    const m = rawStr.match(/(\d{4})(?!.*\d)/);
    if (m) {
      last4 = m[1];
    }
  }

  let brand = get("brand", "scheme", "cardtype", "card_type", "cardbrand", "network");
  if (!brand && rawStr) {
    brand = detectBrand(rawStr);
  }
  if (brand) {
    brand = titleBrand(brand);
  }

  return {
    brand: brand || null,
    last4: last4 ? String(last4).replace(/\D/g, "").slice(-4) : null,
    entryMode: get("entrymode", "entry", "entry_mode", "readtype", "read_type") || null,
    authCode: get("authcode", "auth", "auth_code", "approvalcode", "approval") || null,
    rrn: get("rrn", "retrievalref", "reference", "ref") || null,
    raw: rawStr || null
  };
};

// 返回结构化卡信息或 null（无 cardPayInfo 时）
export function parseCardPayInfo(raw) {
  if (raw === null || raw === undefined || raw === "") {
    return null;
  }
  if (typeof raw === "object") {
    return normalizeCard(raw, "");
  }
  const str = String(raw).trim();
  if (!str) {
    return null;
  }
  if (/^[[{]/.test(str)) {
    try {
      return normalizeCard(JSON.parse(str), str);
    } catch (e) {
      /* not JSON — fall through */
    }
  }
  const pairs = {};
  let matched = false;
  str.split(/[;,|&\n\r\t]+/).forEach(seg => {
    const idx = seg.search(/[:=]/);
    if (idx > 0) {
      const key = seg.slice(0, idx).trim();
      const val = seg.slice(idx + 1).trim();
      if (key) {
        pairs[key] = val;
        matched = true;
      }
    }
  });
  return normalizeCard(matched ? pairs : {}, str);
}

// "Visa ···· 1234" / "···· 1234" / "Visa" / null
export function formatCardLabel(card) {
  if (!card) {
    return null;
  }
  const dots = card.last4 ? `···· ${card.last4}` : "";
  if (card.brand && dots) {
    return `${card.brand} ${dots}`;
  }
  return card.brand || dots || null;
}

// 便捷：从订单对象直接得到卡标签（cardPayInfo 可能在订单或开门 DTO 上）
export function orderCardLabel(order) {
  if (!order) {
    return null;
  }
  const raw = order.cardPayInfo || (order.ordersOpenDoorDTO && order.ordersOpenDoorDTO.cardPayInfo);
  return formatCardLabel(parseCardPayInfo(raw));
}

export default { parseCardPayInfo, formatCardLabel, orderCardLabel };
